import chalk from 'chalk';
import * as _ from 'lodash';
import * as pako from 'pako';
import * as utils from '../utils';
import * as config from './config';
import * as constants from './constants';
import * as httpRequest from './httpRequest';
import * as testimCustomToken from './testimCustomToken';
import pRetry from 'p-retry';
import hash from 'object-hash';
import { ArgError } from '../errors';
import { getLogger } from './logger';
import { getBuckets } from './bucketConfig';
import { TestPlanRunner } from '../runners/TestPlanRunner';
import type { SerializedSlot } from '../services/concurrencyCompanyService';
import type { GridSerializationInput } from '../services/gridService';
import type { AwaitedReturnType } from '../utils';
import type { RawGrid } from 'services/src/commons/mongo/models/grid';
import type { LightweightSettings, RunnerOptions } from '../runOptions';
import type { TestRunStatus, TestRunStatusTestResult } from '../testRunStatus';
import type { RawBranchesData } from 'services/src/commons/mongo/models/branchesData';
import type { Defaults } from 'services/src/commons/mongo/types/DbProject';
import type { ApplitoolsIntegrationData, LambdaTestIntegrationData } from 'clickim/src/common/api/testimVisualTestingApi';
import type { TestBundle } from 'clickim/src/common/api/testimTestApi';
import type { RawMobileAppVersion } from 'services/src/commons/mongo/models/mobileAppVersion';
import type { ICustomCapabilities } from 'services/src/commons/mongo/models/customCapabilities';
import type { TestOptimization } from '../../../services/src/commons/mongo/models/testOptimization.ts';
import type { MobileAppWithVersion } from '../../../services/src/commons/mongo/mongoDataLayer/mobileApps';

const runnerVersion = utils.getRunnerVersion();
const logger = getLogger('testim service api');

const DEFAULT_REQUEST_RETRY = 3;
const GRID_PATH = '/grid';

type TestStatus = typeof constants.runnerTestStatus[keyof typeof constants.runnerTestStatus];

// #region utilities
async function getDefaultHeaders() {
    const bearerToken = await testimCustomToken.getCustomTokenV3();
    if (!bearerToken) {
        throw new Error('Failed to get token from server');
    }

    return {
        [constants.X_HEADER_RUNNER_ID]: TestPlanRunner.id,
        Authorization: `Bearer ${bearerToken}`,
    };
}

async function postAuth<T = any>({
    url, body, headers = {}, timeout, retry,
}: { url: string; body: object; headers?: { [key: string]: any }; timeout?: number; retry?: number }) {
    const tokenHeaders = await getDefaultHeaders();
    const finalHeaders = { ...headers, ...tokenHeaders };
    return httpRequest.post<T>({
        url: `${config.SERVICES_HOST()}${url || ''}`,
        body,
        headers: finalHeaders,
        timeout,
        retry,
    });
}

async function postAuthFormData<T = any>(url: string, fields: { [fieldName: string]: any }, files: httpRequest.Files, headers: object = {}, timeout: number | undefined = undefined) {
    const tokenHeaders = await getDefaultHeaders();
    const finalHeaders = { ...headers, ...tokenHeaders };
    return await httpRequest.postForm<T>(`${config.SERVICES_HOST()}${url || ''}`, fields, files, finalHeaders, timeout);
}

async function putAuth<T = any>(url: string, body: object) {
    const headers = await getDefaultHeaders();
    return await httpRequest.put<T>(`${config.SERVICES_HOST()}${url || ''}`, body, headers);
}

async function deleteAuth<T = any>(url: string, body: object) {
    const headers = await getDefaultHeaders();
    return await httpRequest.deleteReq<T>(`${config.SERVICES_HOST()}${url || ''}`, body, headers);
}

async function getTextWithAuth(url: string, query?: string | object | null) {
    const headers = await getDefaultHeaders();
    return await httpRequest.getText(`${config.SERVICES_HOST()}${url || ''}`, query || undefined, headers);
}
async function getWithAuth<T = any>(url: string, query?: string | object | null, options?: { isBinary?: boolean; skipProxy?: boolean }, timeout?: number) {
    const headers = await getDefaultHeaders();
    return await httpRequest.get<T>(`${config.SERVICES_HOST()}${url || ''}`, query || undefined, headers, timeout, options);
}
// #endregion

export function getS3Artifact<T = any>(url: string, timeout?: number) {
    return pRetry(() => getWithAuth<T>(`/storage${url}`, null, { isBinary: true }, timeout), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function getFileUrl<T = any>(query: string, timeout?: number) {
    return pRetry(() => getWithAuth<T>(`/files/fileUrl?${query}`, null, { isBinary: true }, timeout), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function getS3ArtifactText(url: string) {
    return pRetry(() => getTextWithAuth(`/storage${url}`));
}

export async function reportRunnerStarted(projectId: string, testOptimization: TestOptimization) {
    return await postAuth<void>({ url: '/test-runner', body: { projectId, testOptimization } });
}

export async function reportRunnerFinished(projectId: string) {
    return await deleteAuth<void>('/test-runner', { projectId });
}

// TODO: unused?
export async function getTestPlan(projectId: string, testPlanNames: string[]) {
    //TODO: need to be checked after 3 months to prevent users from using old version
    const parseProperty = (dataValue?: string | string[] | null): string[] => (dataValue === null ? [] : (typeof (dataValue) === 'string' && JSON.parse(dataValue)) || dataValue);
    type TestPlans = AwaitedReturnType<typeof import('services/src/test-plan/service')['testPlan']['getAll']>;
    const body = await pRetry(() => getWithAuth<TestPlans>('/testPlan', { projectId, name: testPlanNames.join(',') }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
    return body.map(({ testConfigIds, beforeAllLabels, testLabels, afterAllLabels, ...testPlan }) => Object.assign(testPlan, {
        testConfigIds: parseProperty(testConfigIds),
        beforeAllLabels: parseProperty(beforeAllLabels),
        testLabels: parseProperty(testLabels),
        afterAllLabels: parseProperty(afterAllLabels),
    }));
}

export async function loadSfdcCredential({ branch, projectId }: { projectId: string; branch: string }) {
    const branchData = await pRetry(() => getWithAuth<RawBranchesData>(`/branch/branchData/${encodeURIComponent(branch)}`, {
        projectId,
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
    return branchData?.sfdcCredential;
}

interface LoadTestParams { testId: string; branch: string; projectId: string; skipSharedSteps?: boolean; useBranchMap?: boolean }

export function loadTest({ testId, branch, projectId, skipSharedSteps = false, useBranchMap = true }: LoadTestParams) {
    return pRetry(() => getWithAuth<TestBundle>(`/test/${testId}`, {
        projectId,
        branch,
        skipSharedSteps,
        useBranchMap,
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function saveTestPlanResult(projectId: string, testPlanId: string, result: { success: boolean; executions: { executionId: string; status: boolean }[]; executionId: string }) {
    return pRetry(() => postAuth<void>({ url: '/testPlan/result', body: { projectId, testPlanId, result } }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function updateTestStatus(projectId: string, executionId: string, testId: string, resultId: string, status: string, data: object, retries = DEFAULT_REQUEST_RETRY) {
    return pRetry(() => putAuth<void>('/result/run/test', {
        runId: executionId,
        testId,
        resultId,
        status,
        projectId,
        runnerVersion,
        ...data,
    }), { retries, factor: 1 });
}

export function updateExecutionTests(
    executionId: string, runnerStatuses: string[], status: TestStatus, reason: string, success: boolean, startTime: number | null, endTime: number | null, projectId: string,
) {
    return pRetry(() => putAuth<void>('/result/run/tests', {
        runId: executionId,
        runnerStatuses,
        status,
        reason,
        success,
        startTime,
        endTime,
        projectId,
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

interface ReportExecutionStartedParams {
    executionId: string;
    projectId: string;
    labels: string | never[];
    startTime: number;
    executions: { [testResultId: string]: TestRunStatusTestResult };
    config: TestRunStatus['execConfig'];
    resultLabels: string[];
    remoteRunId: string | undefined;
    localRunUserId: string | undefined;
    isLocalRun: boolean;
    intersections: RunnerOptions['intersections'];
}
export async function reportExecutionStarted({
    executionId,
    projectId,
    labels,
    startTime,
    executions,
    config: _config,
    resultLabels,
    remoteRunId,
    localRunUserId,
    isLocalRun,
    intersections,
}: ReportExecutionStartedParams) {
    const { isCi: isCiRun } = await import('../cli/isCiRun');
    return postAuth<void>({
        timeout: 90000,
        url: '/result/run',
        body: {
            runId: executionId,
            projectId,
            labels,
            startTime,
            execution: executions,
            status: 'RUNNING',
            config: _config,
            resultLabels,
            remoteRunId,
            intersections,
            metadata: {
                isCiRun,
                localRunUserId,
                isLocalRun,
            },
        },
        retry: 3, // TODO: add a log in the callback
    });
}

export function reportExecutionFinished(
    status: string,
    executionId: string,
    projectId: string,
    success: boolean,
    tmsOptions: object = {},
    remoteRunId: string | undefined = undefined,
    resultExtraData: any = undefined,
) {
    const endTime = Date.now();

    return pRetry(() => putAuth<void>('/result/run', {
        runId: executionId,
        projectId,
        endTime,
        status,
        success,
        tmsOptions,
        remoteRunId,
        resultExtraData,
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export async function getTestPlanTestList(projectId: string, names: string[], planIds: string[], branch: string, intersections: RunnerOptions['intersections']) {
    return pRetry(() => postAuth<AwaitedReturnType<typeof import('services/src/test-plan/service')['testPlan']['getTestPlanTestList']>>({
        url: '/testPlan/list',
        body: { projectId, names, planIds, branch, intersections },
        // people who send insane lists get a timeout :(
        timeout: 120000,
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

interface GetSuiteTestListParams {
    projectId: string;
    labels: string[];
    testIds: string[];
    testNames: string[];
    testConfigNames?: string[];
    suiteNames: string[];
    suiteIds: string[];
    branch: string;
    rerunFailedByRunId?: string;
    testConfigIds?: string[];
    intersections: import('../runOptions').RunnerOptions['intersections'];
}
export function getSuiteTestList({
    projectId, labels, testIds, testNames, testConfigNames, suiteNames, suiteIds, branch, rerunFailedByRunId, testConfigIds, intersections,
}: GetSuiteTestListParams) {
    return pRetry(() => postAuth<import('services/src/suite/service').RunListResult>({
        url: '/suite/v2/list',
        body: {
            projectId,
            labels,
            testIds,
            testNames,
            testConfigNames,
            suiteNames,
            suiteIds,
            branch,
            rerunFailedByRunId,
            testConfigIds,
            intersections,
        },
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export async function getAppDetails({ appId, projectId }: { appId: string; projectId: string }): Promise<RawMobileAppVersion> {
    try {
        const app = await pRetry(
            () => getWithAuth<MobileAppWithVersion>(`/mobile-app/app/${appId}?projectId=${projectId}`),
            { retries: DEFAULT_REQUEST_RETRY, factor: 1 },
        );

        return _.omit(app, 'version');
    } catch (error) {
        throw new Error(`Could not load app with the ID ${appId}`, { cause: error });
    }
}

export async function getUsageForCurrentBillingPeriod(projectId: string) {
    try {
        type UsageCurrentBillingPeriod = AwaitedReturnType<typeof import('services/src/plan/planService')['getUsageStatusForCurrentBillingPeriodByProjectId']>;
        return await pRetry(() => getWithAuth<UsageCurrentBillingPeriod>(`/plan/project/${projectId}/usage-current-billing-period`), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
    } catch (error) {
        logger.error('failed getting usage for current billing period', { projectId, error });
        return undefined;
    }
}

export function isTestResultCompleted(resultId: string, projectId: string, testRetryKey: `${number}:${number}`) {
    return pRetry(() => getWithAuth<{ isComplete: boolean }>(`/result/${resultId}/isComplete`, { projectId, testRetryKey }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function getTestResults(testId: string, resultId: string, projectId: string, branch: string) {
    type TestResult = AwaitedReturnType<typeof import('services/src/test/service').TestService['getTestResultV2']>;
    return pRetry(() => getWithAuth<TestResult>(`/test/v2/${testId}/result/${resultId}`, { projectId, branch }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function keepAliveGrid(projectId: string, slots: { gridId: string; slotId: string }[]) {
    return postAuth<{ status: 'success' }>({
        url: `/grid/keep-alive?reqId=${utils.guid()}`,
        body: { projectId, slots },
        timeout: 10000,
    });
}

export function releaseGridSlot(companyId: string, projectId: string, slotId: string, gridId: string, browser: string) {
    return postAuth<{ status: 'success' }>({
        url: `/grid/release?reqId=${utils.guid()}`,
        body: {
            companyId, projectId, slotId, gridId, browser,
        },
    });
}

export function getHybridGridProvider(body: { companyId: string; gridId: string; maxRetries: number; currentRetry: number; browser: string; usingTunnel: boolean }) {
    return postAuth<AwaitedReturnType<typeof import('services/src/grid/hybridGridService')['getGridProvider']>>({
        url: '/grid/hybrid/provider',
        body,
    });
}

interface GetSlotRequest { testResultId?: string; host?: string; gridId?: string; companyId?: string; projectId: string; gridName?: string; browser?: any; executionId: string; isRunningLocalMode?: boolean }
export function getGridSlot(data: GetSlotRequest) {
    return pRetry(() => getWithAuth<{ status: 'success'; grid: GridSerializationInput } | { status: 'error'; code: string }>(
        '/grid/grid-slot',
        { ...data, reqId: utils.guid() },
    ), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

interface InitializeUserWithAuthParams {
    projectId: string;
    token: string;
    branchName?: string;
    lightweightMode?: LightweightSettings;
    localGrid: boolean;
    publicIp?: string;
    gridId?: string;
    grid?: string;
    clientCliVersion?: string;
    clientNodeVersion?: string;
    clientNpmVersion?: string;
}
export interface InitializedUserWithAuth {
    authData: { token: string; refreshToken: string; uid: string};
    companyByProjectId: AwaitedReturnType<typeof import('services/src/company/companyService')['getCompanyByProjectIdUsingCache']>;
    projectById: NonNullable<AwaitedReturnType<typeof import('services/src/commons/testimCache')['project']['getById']>>;
    allGrids: RawGrid[];
    branchName: string | RawBranchesData | null;
    clientPublicIp?: string;
    clientConfig: {
        clientMessages: [{ text: string; color: string; bgColor: string }] | [];
        serviceUrl: string;
        editorUrl: string;
        region: 'euc1' | 'usw2';
        segmentKey: string;
        [key: string]: any;
    };
}
export async function initializeUserWithAuth({
    projectId, token, branchName, lightweightMode, localGrid,
    publicIp, gridId, grid, clientCliVersion, clientNodeVersion, clientNpmVersion }: InitializeUserWithAuthParams) {
    try {
        return await pRetry(() => httpRequest.post<InitializedUserWithAuth>({
            url: `${config.SERVICES_HOST()}/executions/initialize`,
            body: {
                projectId,
                token,
                branchName: branchName || 'master',
                lightweightMode,
                localGrid,
                publicIp,
                gridId,
                gridName: grid,
                clientCliVersion,
                clientNodeVersion,
                clientNpmVersion,
            },
            // Before adding the WAF to the flow of the initialize endpoint our default timeout was 30 seconds and this was enough. Now, it seems that our work with WAF, sometimes, might take longer
            // than 30 seconds thus we need to increase the timeout to 1 minute.
            timeout: 60_000,
        }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
    } catch (e) {
        logger.error('error initializing info from server', e);
        if (e.message?.includes('Bad Request')) {
            throw new ArgError(
                'Error trying to retrieve CLI token. ' +
                'Your CLI token and project might not match. ' +
                'Please make sure to pass `--project` and `--token` that' +
                ' match to each other or make sure they match in your ~/.testim file.');
        }
        if (e.code?.includes('ENOTFOUND')) {
            throw new ArgError('Due to network connectivity issues, Testim CLI has been unable to connect to the Testim backend.');
        }
        if (e.text) {
            try {
                const errorMessage = JSON.parse(e.text);
                const messageColor = errorMessage.clientMessage.color as typeof chalk.Color || 'red';
                const bgColor = errorMessage.clientMessage.bgColor as typeof chalk.BackgroundColor || 'bgBlack';
                // eslint-disable-next-line no-console
                console.log(chalk[bgColor](chalk[messageColor](errorMessage.clientMessage.text)));
                if (errorMessage.exitCode) {
                    process.exit(errorMessage.exitCode);
                }
            } catch {
                logger.error('Error parsing error message from server', e);
            }
        }
        throw e;
    }
}

export async function getEditorUrl() {
    if (config.EDITOR_URL) {
        return { editorUrl: config.EDITOR_URL };
    }
    try {
        return await pRetry(() => getWithAuth<{ editorUrl: string }>('/system-info/editor-url'), {
            retries: DEFAULT_REQUEST_RETRY,
            onFailedAttempt: error => {
                if (error.attemptNumber >= DEFAULT_REQUEST_RETRY) {
                    throw error;
                }
            },
            factor: 1,
        });
    } catch (err) {
        logger.error('cannot retrieve editor-url from server', { err });
        return { editorUrl: 'https://app.testim.io' };
    }
}

export function getAllGrids(companyId: string) {
    return pRetry(() => getWithAuth<AwaitedReturnType<typeof import('services/src/grid/gridService')['GridService']['getAllGrids']>>('/grid', { companyId }),
        { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export const fetchLambdatestConfig = async (companyId: string, projectId?: string) => pRetry(
    () => getWithAuth<ReturnType<typeof import('services/src/grid/gridService')['GridService']['getLtConfig']>>('/grid/lt/config', { companyId, ...(projectId && { projectId }) }),
    { retries: DEFAULT_REQUEST_RETRY, factor: 1 },
);

export const getLabFeaturesByProjectId = async (projectId: string) => pRetry(
    () => getWithAuth<NonNullable<AwaitedReturnType<typeof import('services/src/commons/testimCache')['labFeatures']['getLabFeaturesByProjectIdV2']>>>(`/labFeature/v2/project/${projectId}`),
    { retries: DEFAULT_REQUEST_RETRY, factor: 1 },
);

export function getRealData(projectId: string, channel: string, query: string) {
    interface RealDataRes { success: boolean; data: AwaitedReturnType<typeof import('services/src/real-data/realDataService')['getDataByModel']> }
    return pRetry(
        () => getWithAuth<RealDataRes>(`/real-data/${channel}?${query}&projectId=${projectId}`),
        { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function updateTestResult(projectId: string, resultId: string, testId: string, testResult: object, remoteRunId?: string) {
    return pRetry(() => postAuth<{ success: boolean }>({
        url: '/result/test',
        body: {
            projectId,
            resultId,
            testId,
            testResult,
            remoteRunId,
        },
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function clearTestResult(projectId: string, resultId: string, testId: string, testResult: object) {
    return pRetry(() => postAuth<{ success: boolean }>({
        url: '/result/test/clear',
        body: {
            projectId,
            resultId,
            testId,
            testResult,
        },
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function saveRemoteStep(projectId: string, resultId: string, stepId: string, remoteStep: object) {
    return pRetry(() => postAuth<{ success: boolean }>({
        url: '/remoteStep',
        body: {
            projectId,
            resultId,
            stepId,
            remoteStep,
        },
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

function relativize(uri: string) {
    return uri.startsWith('/') ? uri : `/${uri}`;
}

function getStorageRelativePath(filePath: string, bucket: string, projectId: string) {
    let fullPath = relativize(filePath);
    if (projectId) {
        fullPath = `/${projectId}${fullPath}`;
    }
    if (bucket) {
        fullPath = `/${bucket}${fullPath}`;
    }

    return fullPath;
}

async function uploadArtifact(projectId: string, testId: string, testResultId: string, content: string, subType: string, mimeType = 'application/octet-stream') {
    let fileSuffix = null;
    if (mimeType === 'application/json') {
        fileSuffix = '.json';
    }
    const fileName = `${subType}_${utils.guid()}${fileSuffix || ''}`;
    const path = `${testId}/${testResultId}/${fileName}`;
    const storagePath = getStorageRelativePath(path, getBuckets().testResultArtifacts, projectId);

    const buffer = Buffer.from(pako.gzip(content, {
        level: 3, // sufficient time/size ratio.
    }));

    const files = {
        file: {
            fileName,
            buffer,
        },
    };

    await pRetry(
        () => postAuthFormData(`/storage${storagePath}`, {}, files, { 'X-Asset-Encoding': 'gzip' }),
        { retries: DEFAULT_REQUEST_RETRY, factor: 1 },
    );
    return storagePath;
}

export const uploadRunDataArtifact = _.memoize(async (projectId: string, testId: string, testResultId: string, runData: object) => {
    if (_.isEmpty(runData)) {
        return undefined;
    }
    return await uploadArtifact(projectId, testId, testResultId, JSON.stringify(runData), 'test-run-data', 'application/json');
}, (projectId, testId, testResultId, runData) => `${hash(runData)}:${testId}:${testResultId}`);

export const updateTestDataArtifact = _.memoize(async (projectId: string, testId: string, testResultId: string, testData: object | undefined, projectDefaults: Defaults) => {
    if (!testData || _.isEmpty(testData)) {
        return undefined;
    }
    const removeHiddenParamsInTestData = () => {
        const testDataValueClone = _.clone(testData);
        if (projectDefaults?.hiddenParams) {
            const hiddenParams = projectDefaults.hiddenParams || [];
            const hiddenParamsArray = Array.isArray(hiddenParams) ? hiddenParams : Object.keys(hiddenParams);
            hiddenParamsArray.forEach((param) => {
                if (testDataValueClone[param as keyof typeof testDataValueClone]) {
                    Object.assign(testDataValueClone, { [param]: constants.test.HIDDEN_PARAM });
                }
            });
        }
        return testDataValueClone;
    };

    return await uploadArtifact(projectId, testId, testResultId, JSON.stringify(removeHiddenParamsInTestData()), 'test-test-data', 'application/json');
}, (projectId, testId, testResultId, testData) => `${hash(testData!)}:${testId}:${testResultId}`);

export function addTestRetry({
    projectId,
    runId,
    testId,
    newResultId,
    originalTestResultId,
    previousTestResultId,
    testResult,
}: { projectId: string; runId: string; testId: string; newResultId: string; originalTestResultId?: string; previousTestResultId?: string; testResult: object }) {
    return pRetry(() => postAuth<{ success: boolean }>({
        url: '/result/test/retry',
        body: {
            projectId,
            newResultId,
            originalTestResultId,
            previousTestResultId,
            testId,
            runId,
            testResult,
        },
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

interface VisualTestingProviderDataMap {
    applitools: ApplitoolsIntegrationData;
    lambdatest: LambdaTestIntegrationData;
}

export async function getVisualTestingIntegrationData<T extends keyof VisualTestingProviderDataMap>(
    projectId: string,
    provider: T,
): Promise<VisualTestingProviderDataMap[T] | null> {
    return await getWithAuth<VisualTestingProviderDataMap[T]>(
        `/integration/visualTesting/connected?projectId=${projectId}&provider=${provider}`,
    );
}

type AcquiredTunnels = AwaitedReturnType<typeof import('services/src/tunnel/service')['tunnelService']['acquireTunnel']>;
export async function getCloudflareTunnel(companyId: string, routes: string[]) {
    try {
        return await putAuth<AcquiredTunnels>('/tunnel', { companyId, routes });
    } catch (err) {
        logger.warn('could\'nt get tunnel.', { err });
        return {} as AcquiredTunnels;
    }
}

export async function forceUpdateCloudflareTunnelRoutes(companyId: string, tunnelId: string) {
    try {
        return await postAuth<void>({ url: `/tunnel/${tunnelId}`, body: { companyId } });
    } catch (err) {
        logger.warn('could\'nt get tunnel.', { err });
        return undefined;
    }
}

export async function deleteCloudflareTunnel(companyId: string, tunnelId: string) {
    try {
        return await deleteAuth<void>(`/tunnel/${tunnelId}`, { companyId });
    } catch (err) {
        logger.warn('could\'nt get tunnel.', { err });
        return undefined;
    }
}

export function updateRemoteRunFailure(body: { remoteRunId: string; projectId: string; token: string; error: string }) {
    return httpRequest.post<void>({ url: `${config.SERVICES_HOST()}/result/remoteRunFailure`, body });
}

interface GetMobileDevicesFromGridParams { projectId: string; projectType: string; gridId: string; companyId: string; selectors?: string }
export async function getMobileDevicesFromGrid({ projectId, projectType, gridId, companyId, selectors }: GetMobileDevicesFromGridParams) {
    let url = `${GRID_PATH}/mobileDevices/${gridId}/${companyId}?projectType=${projectType}`;
    url = selectors ? `${url}&selectors=${encodeURIComponent(selectors)}` : url;
    try {
        return await pRetry(() => getWithAuth<import('services/src/grid/mobileGridService').PlaybackMobileDevice[]>(url, { projectId }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
    } catch (err) {
        logger.warn('could\'nt get devices from grid.', { err });
        return null;
    }
}

interface UploadToGridParams {
    projectId: string;
    gridId: string;
    app: RawMobileAppVersion;
    timeout?: number;
}

interface FixGridAppIDStructureParams {
    projectId: string;
    gridId: string;
    appId: string;
    gridAppId: string;
    gridType: string;
    gridHost: string;
}

export async function uploadAppToGrid({ projectId, gridId, app, timeout }: UploadToGridParams) {
    return pRetry(
        () =>
            postAuth<{ gridAppId: string; gridType: string; gridId: string }>(
                {
                    url: `/mobile-app/app/upload-to-grid?projectId=${projectId}&gridId=${gridId}`,
                    body: app,
                    timeout,
                },
            ),
        {
            retries: DEFAULT_REQUEST_RETRY, factor: 1,
        },
    );
}

export async function fixGridAppIDStructure(body: FixGridAppIDStructureParams) {
    return pRetry(() => postAuth<void>({ url: '/mobile-app/app/fix-grid-app-id', body }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export async function requestCompanySlot(companyId: string, projectId: string) {
    return pRetry(() => postAuth<{ status: 'success'; slotId: string } | { status: 'error'; code: string }>(
        {
            url: '/slot-management/request-slot',
            body: {
                companyId,
                projectId,
            },
        }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export async function releaseCompanySlot(companyId: string, projectId: string, slotId: string) {
    return pRetry(() => postAuth<{ status: 'success' }>({
        url: '/slot-management/release-slot',
        body: {
            companyId,
            projectId,
            slotId,
        },
    }), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export async function updateCompanySlot(companyId: string, projectId: string, slot: Partial<SerializedSlot>) {
    return pRetry(() => putAuth<{ status: 'success' }>('/slot-management/update-slot',
        {
            slotId: slot.slotId,
            companyId,
            projectId,
            slot,
        },
    ), { retries: DEFAULT_REQUEST_RETRY, factor: 1 });
}

export function keepAliveCompanySlots(companyId: string, projectId: string, slots: Partial<SerializedSlot>[]) {
    return postAuth<{ status: 'success' }>({
        url: `/slot-management/keep-alive?reqId=${utils.guid()}`,
        body: { slots, companyId, projectId },
        timeout: 10000,
    });
}

export async function getBrowserStackSessionMetaData(sessionId: string, gridInfo: { user: string; key: string }) {
    return await httpRequest.get<{ automation_session: { public_url: string } }>(
        `${config.BROWSERSTACK_MOBILE_API_URL}/sessions/${sessionId}`,
        undefined,
        { Authorization: `Basic ${Buffer.from(`${gridInfo.user}:${gridInfo.key}`).toString('base64')}` },
    );
}

export async function validateUserAccessKey(data: { userAccessKey: string; projectId: string }) {
    try {
        await getWithAuth('/v2/project/validateUserAccessKey', data);
        return true;
    } catch (error) {
        logger.error('error validating user access key', { error });
        return false;
    }
}

export async function getCustomCapabilitiesByName(projectId: string, capabilitiesName: string): Promise<ICustomCapabilities | undefined> {
    const [capabilities] = await getWithAuth<ICustomCapabilities[]>('/capabilities?', { projectId, name: capabilitiesName });
    return capabilities;
}

// #region LambdaTest SmartUI visual testing uploads (proxied through Services)

export function uploadLambdaTestScreenshot(params: {
    projectId: string;
    buildName: string;
    screenshotName: string;
    screenshotData: string;
    baseline?: boolean;
    smartIgnore?: boolean;
}) {
    return pRetry(
        () => postAuth<{ success: boolean; buildId: string; buildUrl: string }>({
            url: '/integration/visualTesting/lambdatest/screenshot/upload',
            body: params,
            timeout: 60_000,
        }),
        { retries: DEFAULT_REQUEST_RETRY, factor: 1 },
    );
}

interface LambdaTestDomUploadParams {
    projectId: string;
    buildName: string;
    snapshotName: string;
    domContent: string;
    url?: string;
    branch?: string;
    browsers?: string[];
    viewports?: [number, number][];
    mobile?: { devices: string[]; fullPage: boolean; orientation: 'portrait' | 'landscape' };
    matchLevel?: 'Exact' | 'Strict' | 'Content' | 'Layout';
    smartIgnore?: boolean;
    ignoreRegions?: Array<{ left: number; top: number; width: number; height: number }>;
    fullPage?: boolean;
}

interface LambdaTestDomResult {
    success: boolean;
    buildId: string;
    buildUrl: string;
    isNewBaseline?: boolean;
    hasChanges?: boolean;
    mismatchPercentage?: number;
    comparisonStatus?: 'passed' | 'changes_detected' | 'new_baseline' | 'unknown';
}

interface DomComparisonPollResult {
    ready: boolean;
    buildId: string;
    isNewBaseline?: boolean;
    hasChanges?: boolean;
    mismatchPercentage?: number;
    comparisonStatus?: 'passed' | 'changes_detected' | 'new_baseline' | 'unknown';
}

const SMARTUI_UPLOAD_TIMEOUT_MS = 30_000;
const SMARTUI_UPLOAD_RETRIES = 2;
const SMARTUI_POLL_INTERVAL_MS = 3_000;
const SMARTUI_MAX_POLL_TIME_MS = 130_000;

const delay = (ms: number) => new Promise(r => setTimeout(r, ms));

const buildStatusUrl = (buildId: string, branch: string, projectId: string) =>
    `/integration/visualTesting/lambdatest/dom/status?buildId=${buildId}&branch=${encodeURIComponent(branch || '')}&projectId=${projectId}`;

export async function uploadLambdaTestDom(params: LambdaTestDomUploadParams): Promise<LambdaTestDomResult> {
    const uploadResult = await pRetry(
        () => postAuth<{ success: boolean; buildId: string; buildUrl: string; branch: string }>({
            url: '/integration/visualTesting/lambdatest/dom/upload',
            body: params,
            timeout: SMARTUI_UPLOAD_TIMEOUT_MS,
        }),
        { retries: SMARTUI_UPLOAD_RETRIES, factor: 1 },
    );

    if ((uploadResult as any).comparisonStatus) {
        return uploadResult as unknown as LambdaTestDomResult;
    }

    const { buildId, branch } = uploadResult;
    const pollStartTime = Date.now();
    const hasTimedOut = () => (Date.now() - pollStartTime) >= SMARTUI_MAX_POLL_TIME_MS;

    while (!hasTimedOut()) {
        await delay(SMARTUI_POLL_INTERVAL_MS);
        try {
            const result = await getWithAuth<DomComparisonPollResult>(buildStatusUrl(buildId, branch, params.projectId));
            if (result.ready) {
                return {
                    success: true,
                    buildId,
                    buildUrl: uploadResult.buildUrl,
                    isNewBaseline: result.isNewBaseline,
                    hasChanges: result.hasChanges,
                    mismatchPercentage: result.mismatchPercentage,
                    comparisonStatus: result.comparisonStatus,
                };
            }
        } catch {
            // Retry on next poll interval
        }
    }

    return { success: true, buildId, buildUrl: uploadResult.buildUrl, comparisonStatus: 'unknown' };
}

// #endregion
